import React, { useEffect, useState } from 'react'
import { BillingConfig, UpdateBillingConfigDto } from '@/types'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { facturationService } from '@/services/facturation.service'
import { toastError, toastSuccess } from '@/utils/toast'
import { VALIDATION_PATTERNS } from '@/config/constants'

interface BillingConfigSectionProps {
  readOnly?: boolean
}

export const BillingConfigSection: React.FC<BillingConfigSectionProps> = ({
  readOnly = false,
}) => {
  const [config, setConfig] = useState<BillingConfig | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [form, setForm] = useState<UpdateBillingConfigDto>({
    unitPrice: 0,
    vatRate: 20,
    notificationEmail: '',
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    facturationService
      .getBillingConfig()
      .then(data => {
        setConfig(data)
        setForm({
          unitPrice: data.unitPrice,
          vatRate: data.vatRate,
          notificationEmail: data.notificationEmail,
        })
      })
      .catch(error => {
        console.error('Error loading billing config:', error)
      })
      .finally(() => setIsLoading(false))
  }, [])

  const validate = (): boolean => {
    const nextErrors: Record<string, string> = {}

    if (form.unitPrice == null || form.unitPrice < 0) {
      nextErrors.unitPrice = 'Le tarif unitaire doit être un montant positif'
    }

    if (form.vatRate == null || form.vatRate < 0 || form.vatRate > 100) {
      nextErrors.vatRate = 'Le taux de TVA doit être compris entre 0 et 100'
    }

    if (!form.notificationEmail?.trim()) {
      nextErrors.notificationEmail = "L'adresse email est requise"
    } else if (!VALIDATION_PATTERNS.EMAIL.test(form.notificationEmail)) {
      nextErrors.notificationEmail = 'Adresse email invalide'
    }

    setErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  const handleSave = async () => {
    if (!validate()) return

    setIsSaving(true)
    try {
      const updated = await facturationService.updateBillingConfig(form)
      setConfig(updated)
      toastSuccess('Paramètres de facturation enregistrés')
    } catch (error) {
      toastError(
        error instanceof Error
          ? error.message
          : 'Erreur lors de la sauvegarde des paramètres de facturation'
      )
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-[var(--color-text-secondary)]">Chargement...</div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg border border-[var(--color-border)] shadow-sm p-6">
      <div className="space-y-4">
        <div>
          <h2 className="text-xl font-semibold text-[var(--color-text-primary)]">
            Paramètres de facturation
          </h2>
          <p className="text-sm text-[var(--color-text-secondary)] mt-1">
            Tarif trimestriel unitaire par licence, taux de TVA et adresse de notification pour les
            nouvelles factures émises.
          </p>
        </div>

        <div className="space-y-4">
          <Input
            label="Tarif unitaire par licence (EUR / trimestre)"
            type="number"
            min={0}
            step={0.01}
            value={form.unitPrice ?? ''}
            onChange={event =>
              setForm(prev => ({ ...prev, unitPrice: Number(event.target.value) }))
            }
            error={errors.unitPrice}
            disabled={readOnly}
            helperText="Tarif unique — pas de différenciation entre nouvelles licences et renouvellements"
          />

          <Input
            label="Taux de TVA (%)"
            type="number"
            min={0}
            max={100}
            step={0.1}
            value={form.vatRate ?? ''}
            onChange={event => setForm(prev => ({ ...prev, vatRate: Number(event.target.value) }))}
            error={errors.vatRate}
            disabled={readOnly}
          />

          <Input
            label="Email de notification facturation"
            type="email"
            value={form.notificationEmail ?? ''}
            onChange={event =>
              setForm(prev => ({ ...prev, notificationEmail: event.target.value }))
            }
            error={errors.notificationEmail}
            disabled={readOnly}
            helperText="Reçoit un email à chaque nouvelle facture générée (1× par facture)"
          />
        </div>

        {!readOnly && (
          <div className="flex justify-end">
            <Button onClick={handleSave} isLoading={isSaving}>
              Enregistrer la configuration
            </Button>
          </div>
        )}

        {config && readOnly && (
          <p className="text-sm text-[var(--color-text-muted)]">
            Dernière configuration chargée — modification réservée aux administrateurs.
          </p>
        )}
      </div>
    </div>
  )
}
